import AdapterDateFns from "@mui/lab/AdapterDateFns";
import DateRangePicker from "@mui/lab/DateRangePicker";
import LocalizationProvider from "@mui/lab/LocalizationProvider";
import Box from "@mui/material/Box";
import deLocale from "date-fns/locale/ru";
import { useFormikContext } from "formik";
import * as React from "react";
import { ReactComponent as ArrowIcon } from "../../assets/images/arrow-right-blue.svg";
import { ReactComponent as CloseIcon } from "../../assets/images/close.svg";
import "./DatePicker.scss";
import { useSnackbar } from "notistack";
import {
  DEFAULT_STATS_PERIOD,
  INTERESTS_HIDDEN_USER_ID,
} from "../../modules/Statistics/specialClientMockStats";


function DatePicker({ userData }) {
  const curDate = new Date();
  const getInitialDateRange = () => {
    if (userData?.user?.id === INTERESTS_HIDDEN_USER_ID) {
      return [DEFAULT_STATS_PERIOD.date_start, DEFAULT_STATS_PERIOD.date_end];
    }

    return [
      new Date(curDate.getFullYear(), curDate.getMonth() - 1, curDate.getDate()),
      new Date(curDate.getFullYear(), curDate.getMonth(), curDate.getDate()),
    ];
  };
  const [value, setValue] = React.useState(getInitialDateRange);

  const formik = useFormikContext();
  const { enqueueSnackbar } = useSnackbar();

  const RESTRICTED_CLIENT_ID = "d13b7ea6-4c0b-43f6-905f-6780abda0f96";
  const RESTRICTED_CLIENT_ID_2 = "d7de6dcb-228b-479b-9226-855853edc9e5";
  const RESTRICTED_CLIENT_ID_3 = "0aeb7a8a-7120-49ab-b62c-13158dc69304";
  const MIN_STATS_DATE = new Date(2026, 1, 1);
  const MIN_STATS_DATE_2 = new Date(2026, 2, 1);
  const MIN_STATS_DATE_3 = new Date(2026, 5, 1);

  const getMinStatsDate = (userId) => {
    const candidates = [];
    if (userId === RESTRICTED_CLIENT_ID) candidates.push(MIN_STATS_DATE);
    if (userId === RESTRICTED_CLIENT_ID_2) candidates.push(MIN_STATS_DATE_2);
    if (userId === RESTRICTED_CLIENT_ID_3) candidates.push(MIN_STATS_DATE_3);
    const fromSettings = userData?.user?.cabinet_visibility?.stats_visible_from;
    if (fromSettings) {
      const parts = String(fromSettings).split("-").map(Number);
      if (parts.length === 3 && parts.every((n) => !Number.isNaN(n))) {
        candidates.push(new Date(parts[0], parts[1] - 1, parts[2]));
      }
    }
    if (!candidates.length) return null;
    return new Date(Math.max(...candidates.map((d) => d.getTime())));
  };

  const getMinStatsDateLabel = (userId) => {
    const minDate = getMinStatsDate(userId);
    if (!minDate) return null;
    const dd = String(minDate.getDate()).padStart(2, "0");
    const mm = String(minDate.getMonth() + 1).padStart(2, "0");
    const yyyy = minDate.getFullYear();
    return `${dd}.${mm}.${yyyy}`;
  };

  const adjustDateEnd = (dateEnd, userId) => {
    if (!dateEnd) return dateEnd;

    const SPECIAL_CLIENT_ID = "a1856853-7f15-427a-ad58-3c86aca153de";

    if (userId === SPECIAL_CLIENT_ID) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const endDate = new Date(dateEnd);
      endDate.setHours(0, 0, 0, 0);

      if (endDate.getTime() === today.getTime()) {
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        return yesterday;
      }
    }

    return dateEnd;
  };

  React.useEffect(() => {
    if (!formik) {
      return;
    }

    let startDate = value[0];
    const endDate = value[1];

    const minDate = getMinStatsDate(userData?.user?.id);
    if (minDate && startDate && startDate < minDate) {
      startDate = minDate;
    }

    const adjustedEndDate = adjustDateEnd(endDate, userData?.user?.id);

    formik.setFieldValue("media_plan.date_start", startDate);
    formik.setFieldValue("media_plan.date_end", adjustedEndDate);

    if (startDate !== value[0] || adjustedEndDate !== value[1]) {
      setValue([startDate, adjustedEndDate]);
    }
  }, []);

  return (
    <div className="DatePicker">
      <LocalizationProvider dateAdapter={AdapterDateFns} locale={deLocale}>
        <DateRangePicker
          label="Advanced keyboard"
          value={value}
          mask="__.__.____"
          minDate={getMinStatsDate(userData?.user?.id) ?? undefined}
          onChange={(newValue) => {
            let startDate = newValue[0];
            let endDate = newValue[1];

            const minDate = getMinStatsDate(userData?.user?.id);
            if (minDate && startDate && startDate < minDate) {
              startDate = minDate;

              enqueueSnackbar(`Просмотр статистики возможен только с ${getMinStatsDateLabel(userData?.user?.id)}`, {
                variant: "info",
                autoHideDuration: 3000,
                anchorOrigin: { vertical: "top", horizontal: "right" },
              });
            }

            let adjustedEndDate = endDate;

            if (endDate) {
              adjustedEndDate = adjustDateEnd(endDate, userData?.user?.id);
            }

            if (startDate) {
              formik.setFieldValue(
                "media_plan.date_start",
                startDate
              );
            }
            if (adjustedEndDate) {
              formik.setFieldValue(
                "media_plan.date_end",
                adjustedEndDate
              );
            }
            setValue([startDate, adjustedEndDate]);
          }}
          renderInput={(startProps, endProps) => (
            <React.Fragment>
              <input
                className="date-picker"
                ref={startProps.inputRef}
                {...startProps.inputProps}
                placeholder="Начало периода"
              />
              <Box sx={{ mx: 1 }}>
                <button
                  type="button"
                  onClick={() => {
                    formik.setFieldValue(
                      "media_plan.date_start",
                      null
                    );
                    setValue([null, value[1]]);
                  }}>
                  <CloseIcon />
                </button>
              </Box>
              <Box sx={{ mx: 1 }}>
                <ArrowIcon />
              </Box>
              <input
                className="date-picker"
                ref={endProps.inputRef}
                {...endProps.inputProps}
                placeholder="Окончание периода"
              />
              <Box sx={{ mx: 1 }}>
                <button
                  type="button"
                  onClick={() => {
                    formik.setFieldValue(
                      "media_plan.date_end",
                      null
                    );
                    setValue([value[0], null]);
                  }}>
                  <CloseIcon />
                </button>
              </Box>
            </React.Fragment>
          )}
        />
      </LocalizationProvider>
    </div>
  );
}

export default DatePicker;
